import { useEffect, useRef, useState } from "react";
import { TSubscriptionRow } from "../../../../../services/backoffice/backoffice-service";
import { useRouteNavigation } from "../../../../../lib/common/routes";
import { TProcessStates } from "../../../../../lib/types/general";
import { Spinner } from "../../../../common/spinner";

type PaymentRecordMenuProps = {
    userId: number;
    onCloseMenu: () => void;
    setEditingUserId: (id: number) => void;
};

export const SubscriptionMenu = ({
    userId,
    onCloseMenu,
    setEditingUserId,
}: PaymentRecordMenuProps) => {
    const { navigateToRoute } = useRouteNavigation();

    return (
        <div className="dropdown-menu show actionsMenu">
            <button
                className="dropdown-item"
                onClick={(e) => {
                    e.stopPropagation();
                    onCloseMenu();
                    navigateToRoute(
                        "adminUserInfoLab",
                        userId.toString(),
                        true
                    );
                }}
            >
                <i className="fa-solid fa-user me-2 fa-xs"></i>
                <small>View Profile</small>
            </button>
            <button
                className="dropdown-item text-primary"
                onClick={() => {
                    setEditingUserId(userId);
                    onCloseMenu();
                }}
            >
                <i className="fa-solid fa-circle-plus me-2 fa-xs"></i>
                <small>Edit Credits</small>
            </button>
        </div>
    );
};

type TSubscriptionRowProps = {
    subscriptionRow: TSubscriptionRow;
    editCreditCallback: (userid: number, credits: number) => void;
    isEditingCredit: boolean;
    setEditingUserId: (userid: number | null) => void;
};

export const SubscriptionRow = ({
    subscriptionRow,
    editCreditCallback,
    setEditingUserId,
    isEditingCredit,
}: TSubscriptionRowProps) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuButtonRef = useRef<HTMLTableCellElement>(null);
    const newCreditRef = useRef<HTMLInputElement>(null);
    const [currWorkingState, setCurrWorkingState] =
        useState<TProcessStates>("done");

    const userStatus = subscriptionRow.enabled === 1 ? "active" : "blocked";
    const handleSave = async () => {
        const inputCredit = Number(newCreditRef.current?.value);
        if (isNaN(inputCredit)) return;
        setCurrWorkingState("working");
        await editCreditCallback(subscriptionRow.userid, inputCredit);
        setEditingUserId(null);
        setCurrWorkingState("done");
    };

    const handleCancel = () => {
        setEditingUserId(null);
    };

    const closeMenu = () => {
        setMenuOpen(false);
        document.removeEventListener("mousedown", handleClickOutside);
    };

    const openMenu = () => {
        setMenuOpen(true);
        document.addEventListener("mousedown", handleClickOutside);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (
            menuButtonRef.current &&
            !menuButtonRef.current.contains(event.target as Node)
        ) {
            closeMenu();
        }
    };

    const handleMenuClickOpen = () => {
        let currMenuOpenValue = menuOpen;
        setMenuOpen((prev) => {
            currMenuOpenValue = !prev;
            return !prev;
        });
        if (currMenuOpenValue) {
            openMenu();
        }
        if (!currMenuOpenValue) {
            closeMenu();
        }
    };

    useEffect(() => {
        return () => {
            closeMenu();
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <tr className="table-light align-middle">
            <th scope="row">{subscriptionRow.name}</th>
            <td className="text-end">
                {isEditingCredit ? (
                    <div className="hstack">
                        <input
                            type="number"
                            ref={newCreditRef}
                            className="form-control me-2"
                            defaultValue={subscriptionRow.credits}
                            aria-label="Edit Credits"
                            disabled={currWorkingState === "working"}
                        />
                        <button
                            className="btn btn-sm btn-primary me-1"
                            onClick={handleSave}
                            disabled={currWorkingState === "working"}
                        >
                            Save
                        </button>
                        <button
                            className="btn btn-sm btn-danger"
                            onClick={handleCancel}
                            disabled={currWorkingState === "working"}
                        >
                            Cancel
                        </button>
                    </div>
                ) : currWorkingState === "working" ? (
                    <Spinner spinnerColor="text-primary" />
                ) : (
                    <span>{subscriptionRow.credits}</span>
                )}
            </td>
            <td>
                <div>
                    <p className="mb-1">
                        <span className="badge bg-primary text-uppercase me-2">
                            {subscriptionRow.payment_method?.brand}
                        </span>
                        <span>
                            •••• {subscriptionRow.payment_method?.last4}
                        </span>
                    </p>
                    <small className="text-muted m-0">
                        Exp: {subscriptionRow.payment_method?.exp_month}/
                        {subscriptionRow.payment_method?.exp_year}
                    </small>
                </div>
            </td>
            <td style={{ fontSize: "small" }}>
                {new Date(subscriptionRow.update_date).toLocaleDateString(
                    "en-US",
                    {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    }
                )}
            </td>
            <td>
                <span
                    className={`badge ${
                        userStatus === "active" ? "bg-success" : "bg-danger"
                    } text-capitalize`}
                >
                    {userStatus}
                </span>
            </td>
            <td className="position-relative text-center" ref={menuButtonRef}>
                <button
                    className="btn btn-sm btn-light rounded-circle"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClickOpen();
                    }}
                >
                    <i className="fa-solid fa-ellipsis"></i>
                </button>

                {menuOpen && (
                    <SubscriptionMenu
                        userId={subscriptionRow.userid}
                        onCloseMenu={closeMenu}
                        setEditingUserId={setEditingUserId}
                    />
                )}
            </td>
        </tr>
    );
};

type TSubscriptionsListProps = {
    subscriptionList: TSubscriptionRow[];
    editCreditCallback: (userid: number, credits: number) => void;
};

export const SubscriptionRecordList = ({
    subscriptionList,
    editCreditCallback,
}: TSubscriptionsListProps) => {
    const [editingUserId, setEditingUserId] = useState<number | null>(null);
    return (
        <div className="table-responsive rounded-3 pb-lg-5">
            <table className="table table-striped-columns table-hover table-bordered">
                <thead className="table-dark align-middle">
                    <tr>
                        <th scope="col">Name</th>
                        <th scope="col">Available Credits</th>
                        <th scope="col">Payment Info</th>
                        <th scope="col">Last Updated</th>
                        <th scope="col">Is User Blocked?</th>
                        <th scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    {subscriptionList.map((subscriptionRow) => (
                        <SubscriptionRow
                            key={subscriptionRow.userid}
                            subscriptionRow={subscriptionRow}
                            editCreditCallback={editCreditCallback}
                            isEditingCredit={
                                editingUserId === subscriptionRow.userid
                            }
                            setEditingUserId={setEditingUserId}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};
